import React, { useState, useEffect } from 'react';
import Navbar from "../components/common/Navbar";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory } from 'react-router-dom';
import {faFilePdf} from '@fortawesome/free-solid-svg-icons'
import { generatePDF } from '../utils/pdfGenerator';
import {
  calculateFixedScenario,
  calculateMultiYearFixedScenario,
  calculateHybridScenario,
  calculateMultiYearHybridScenario,
  calculateHybridPortfolioScenario,
  calculateMultiYearHybridPortfolioScenario
} from '../utils/feeCalculations';

const feeData = {
  Fixed: {
    "Agreya Momentum": { managementFee: 2.0, performanceFee: 0, hurdleRate: "NA" },
    "Agreya Total Return": { managementFee: 2.0, performanceFee: 0, hurdleRate: "NA" },
    "Agreya Multi-Asset": { managementFee: 0.5, performanceFee: 0, hurdleRate: "NA" },
    "Agreya Multi-Asset Enhancer": { managementFee: 1.5, performanceFee: 0, hurdleRate: "NA" },
    "Agreya Total Return Enhancer": { managementFee: 2.0, performanceFee: 0, hurdleRate: "NA" },
    "Agreya Momentum Enhancer": { managementFee: 2.0, performanceFee: 0, hurdleRate: "NA" },
    "Agreya Value Momentum Strategy": { managementFee: 2.0, performanceFee: 0, hurdleRate: "NA" }
  },
  Hybrid: {
    "Agreya Momentum": { managementFee: 1.2, performanceFee: 15, hurdleRate: "10%" },
    "Agreya Total Return": { managementFee: 1.2, performanceFee: 15, hurdleRate: "10%" },
    "Agreya Total Return Enhancer": { managementFee: 1.2, performanceFee: 20, hurdleRate: "12%" },
    "Agreya Momentum Enhancer": { managementFee: 1.2, performanceFee: 20, hurdleRate: "12%" },
    "Portfolio Return Optimiser": { managementFee: 0.2, performanceFee: 20, hurdleRate: "0%" },
    "Agreya Value Momentum Strategy": { managementFee: 1.2, performanceFee: 15, hurdleRate: "10%" }
  }
};

function FeeSelection() {
  const [investmentAmount, setInvestmentAmount] = useState('5000000');
  const [formattedAmount, setFormattedAmount] = useState('50 lakhs');
  const [errorMessage, setErrorMessage] = useState('');
  const [showTable, setShowTable] = useState(true);
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Fee Model | Agreya Capital';
  }, []);

  useEffect(() => {
    const storedAmount = localStorage.getItem('investmentAmount');
    if (storedAmount) {
      setInvestmentAmount(storedAmount);
      setFormattedAmount(formatAmount(storedAmount));
      setIsSubmitted(true);

    }
    else{
      setIsSubmitted(true);
    }
  }, []);

  const handleInvestmentChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) { 
    setInvestmentAmount(value);
    }
    localStorage.setItem('investmentAmount', value); 
    setFormattedAmount(formatAmount(value)); 
    setIsSubmitted(false);
    setShowTable(true);  
  };

  const handleSubmit = () => {
    const investmentValue = parseFloat(investmentAmount);
    setIsSubmitting(true); 

   
    if (!investmentAmount || isNaN(investmentValue) || investmentValue < 5000000) {
      setErrorMessage('Kindly enter an amount above 50 lakhs.');
      setShowTable(true); 
      return;
    }
    setErrorMessage('');
    setShowTable(true);  
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitting(false), 1000);
  };

  const handleResetToDefault = () => {
    const defaultAmount = '5000000'; 
    setInvestmentAmount(defaultAmount);
    setFormattedAmount(formatAmount(defaultAmount)); 
    setErrorMessage('');
    setIsSubmitted(true);
  };
  
  const handleClick = async (feeModel, approach) => {
    if (!isSubmitted) {
      setErrorMessage('Please submit the investment amount first.');
      return;
    }
    const investmentValue = parseFloat(investmentAmount);

    if (!investmentValue || isNaN(investmentValue) || investmentValue < 5000000) {
      setErrorMessage('Kindly enter an amount above 50 lakhs.');
      return;
    }

    let feeDetails;
    if (feeModel === 'MultiYearHybrid') {
      feeDetails = feeData.Hybrid[approach];
    } if (feeModel === 'MultiYearFixed') {
      feeDetails = feeData.Fixed[approach];
    } else if (feeData[feeModel]?.[approach]) {
      feeDetails = feeData[feeModel][approach];
    }

    if (!feeDetails) {
      console.error(`No fee details found for model: ${feeModel}, approach: ${approach}`);
      return;
    }

    try {
      const assumptions = {
        a: investmentValue || 0,
        b: feeDetails?.managementFee || 2.5,
        c: 0.20,
        d: 0.10,
        e: feeDetails?.performanceFee || 2.5,
        f: feeDetails?.hurdleRate || 2.5,
      };

      let scenarios = [];
      if (feeModel === "Fixed") {
        const scenario1 = calculateFixedScenario(investmentValue, 0.2, assumptions);
        const scenario2 = calculateFixedScenario(investmentValue, -0.2, assumptions);
        const scenario3 = calculateFixedScenario(investmentValue, 0, assumptions);
        scenarios = [scenario1, scenario2, scenario3];
      } else if (feeModel === "MultiYearFixed" && approach !== "Portfolio Return Optimiser") {
        const scenarioRates = [-0.26, 0.79, 0.22, 0, 0.4];
        scenarios = calculateMultiYearFixedScenario(investmentValue, scenarioRates, assumptions, feeDetails);
      } else if (feeModel === "MultiYearHybrid" && approach === "Portfolio Return Optimiser") {
        const scenarioRates = [-260000, 790000, 220000, 0, 400000];
        scenarios = calculateMultiYearHybridPortfolioScenario(investmentValue, scenarioRates, assumptions, feeDetails);
      } else if (feeModel === "Hybrid" && approach === "Portfolio Return Optimiser") {
        const scenarioRates = [-260000, 0, 220000];
        scenarios = calculateHybridPortfolioScenario(investmentValue, scenarioRates, assumptions, feeDetails);
      } else if (feeModel === "Hybrid" && approach !== "Portfolio Return Optimiser") {
        const scenario1 = calculateHybridScenario(investmentValue, 0.2, assumptions, feeDetails);
        const scenario2 = calculateHybridScenario(investmentValue, -0.2, assumptions, feeDetails);
        const scenario3 = calculateHybridScenario(investmentValue, 0, assumptions, feeDetails);
        scenarios = [scenario1, scenario2, scenario3];
      } else if (feeModel === "MultiYearHybrid" && approach !== "Portfolio Return Optimiser") {
        const scenarioRates = [-0.26, 0.79, 0.22, 0, 0.4];
        scenarios = calculateMultiYearHybridScenario(investmentValue, scenarioRates, assumptions, feeDetails);
      }

      await generatePDF(feeModel, approach, scenarios, assumptions, feeDetails);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setErrorMessage('Error generating PDF. Please try again.');
    }
  };

  const formatAmount = (amount) => {
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) {
      return;
    }
    if (parsedAmount < 5000000) {
      return;
    }
    if (parsedAmount >= 10000000) {
      const formatted = parsedAmount / 10000000;
      return `${formatted} crore`;
    } else if (parsedAmount >= 100000) {
      const formatted = parsedAmount / 100000;
      return `${formatted} lakhs`;
    } else {
      return `${parsedAmount}`;  
    }
  };
  
  return (
    <>
      <header className="header-area">
        <Navbar />
      </header>
      <main id="main-content">
      <div className="fee-model-container">
      <h2 className="fee-model-p"style={{lineHeight:'1.8', color:'#233d63', fontWeight: 1000, textAlign: 'center'}}>
  Agreya offers the following fee models:<br/>
  a) Fixed Fee Model<br/>
  b) Hybrid Fee Model
</h2>


            <p className='ap' style={{ textAlign:'center',marginTop:'2%'}}>The  detailed structure and fee illustrations given in the below table</p>
        {showTable && (
          <>
            <div className="fee-model">
              <table>
              <thead>
      <tr>
        <th rowSpan="2">Investment Approaches</th>
        <th colSpan="1">Fixed Fee</th>
        <th colSpan="2">Fee Illustration *</th>
        <th colSpan="3">Hybrid Fee</th>
        <th colSpan="2">Fee Illustration *</th>
      </tr>
      <tr>
        <th>Management Fee</th>
        <th>One Year</th>
        <th>Multi-Year</th>
        <th>Management Fee</th>
        <th>Performance Fee</th>
        <th>Hurdle Rate</th>
        <th>One Year</th>
        <th>Multi-Year</th>
      </tr>
    </thead>
                <tbody>
                    {Object.keys(feeData.Fixed).map((approach, index) => {
                      const fixed = feeData.Fixed[approach] || null;
                      const hybrid = feeData.Hybrid[approach] || null;
                      return (
                        <tr key={index}>
                          <td>{approach}</td>
                          <td>{fixed ? `${fixed.managementFee}% p.a.` : '—'}</td>
                          <td>
                            {fixed ? (
                              <button aria-label={`${approach} Fixed Fee PDF`} onClick={() => handleClick('Fixed', approach)}>
                                <FontAwesomeIcon icon={faFilePdf} style={{ color: "#ff0000", fontSize: "1.5em" }} />
                              </button>
                            ) : '—'}
                          </td>
                          <td>
                            {fixed ? (
                              <button aria-label={`${approach} Multi-Year Fixed Fee PDF`} onClick={() => handleClick('MultiYearFixed', approach)}>
                                <FontAwesomeIcon icon={faFilePdf} style={{ color: "#ff0000", fontSize: "1.5em" }} />
                              </button>
                            ) : '—'}
                          </td>

                          <td>{hybrid ? `${hybrid.managementFee}% p.a.` : '—'}</td>
                          <td>{hybrid ? `${hybrid.performanceFee}%` : '—'}</td>
                          <td>{hybrid ? hybrid.hurdleRate : '—'}</td>
                          <td>
                            {hybrid ? (
                              <button aria-label={`${approach} Hybrid Fee PDF`} onClick={() => handleClick('Hybrid', approach)}>
                                <FontAwesomeIcon icon={faFilePdf} style={{ color: "#ff0000", fontSize: "1.5em" }} />
                              </button>
                            ) : '—'}
                          </td>
                          <td>
                            {hybrid ? (
                              <button aria-label={`${approach} Multi-Year Hybrid Fee PDF`} onClick={() => handleClick('MultiYearHybrid', approach)}>
                                <FontAwesomeIcon icon={faFilePdf} style={{ color: "#ff0000", fontSize: "1.5em" }} />
                              </button>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}

                  
                    {Object.keys(feeData.Hybrid).map((approach, index) => {
                      if (!feeData.Fixed[approach]) {
                        const hybrid = feeData.Hybrid[approach];
                        return (
                          <tr key={`hybrid-${index}`}>
                            <td>{approach}</td>
                            <td>—</td>
                            <td>—</td>
                            <td>—</td>
                            <td>{`${hybrid.managementFee}% p.a.`}</td>
                            <td>{`${hybrid.performanceFee}%`}</td>
                            <td>{hybrid.hurdleRate}</td>
                            <td>
                              <button aria-label={`${approach} Hybrid Fee PDF`} onClick={() => handleClick('Hybrid', approach)}>
                                <FontAwesomeIcon icon={faFilePdf} style={{ color: "#ff0000", fontSize: "1.5em" }} />
                              </button>
                            </td>
                            <td>
                              <button aria-label={`${approach} Multi-Year Hybrid Fee PDF`} onClick={() => handleClick('MultiYearHybrid', approach)}>
                                <FontAwesomeIcon icon={faFilePdf} style={{ color: "#ff0000", fontSize: "1.5em" }} />
                              </button>
                            </td>
                          </tr>
                        );
                      }
                      return null;
                    })}
                  </tbody>

              </table>
            
            </div>
            <p className='ap' style={{ fontStyle: 'italic', textAlign: 'center', marginTop: '20px' }}>*For capital contribution of Rs.50 lakhs.</p>
          </>
        )}

        
        {/* Additional Information Section */}
        <div className="additional-info">

            {/* Download Fee Calculation Tool Button */}
            <button
              className="download-excel-btn"
              onClick={() => {
                const link = document.createElement('a');
                link.href ="/files/Fee _calculation_illustration.xlsx";
                link.download = 'Agreya PMS_Fee Calculation Tool.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              style={{
                marginTop: '15px',
                padding: '12px 20px',
                backgroundColor: '#8F6117',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                width: '100%',
                maxWidth: '500px',
                fontSize: '14px',
                display: 'block',
                margin: '15px auto 0',
              }}
            >
              Click here to download Fee calculation tool
            </button>
          </div>
      </div>
      </main>
    </>
  );
}
export default FeeSelection;